import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sun, Thermometer, Landmark, Gift, Users, MousePointerClick, Bell, CalendarDays,
  ChevronRight, Check, Palmtree, Send, ShieldCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Logo from '../components/Logo';
import { MarketingNav, MarketingFooter } from '../components/MarketingChrome';
import { MONTHLY } from '../lib/pricing';
import useTrialDays from '../lib/useTrialDays';

/*
 * "Warm Hive" marketing landing page.
 * Design: design_handoff_holidayhive/HolidayHive Landing v2.dc.html (+ README.md).
 * The page is intentionally light-only — it uses the fixed hive-* palette rather than
 * the light/dark theme tokens, so it renders identically whichever theme is active.
 * The nav and footer live in components/MarketingChrome.jsx, shared with /terms and /privacy.
 * All cards in the product collage are static decorative mocks — no data fetching.
 */

const CTA_PRIMARY =
  'inline-flex items-center justify-center rounded-full bg-hive-orange font-poppins font-semibold text-white ' +
  'transition-colors hover:bg-hive-orange-hover focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-hive-orange focus-visible:ring-offset-2';

/* ── Small building blocks ─────────────────────────────────────────── */


// Navy floating callout pill that sits over a collage card.
const CalloutPill = ({ children, icon: Icon, className = '' }) => (
  <div
    className={`absolute z-10 flex items-center gap-1.5 whitespace-nowrap rounded-full bg-hive-navy px-[18px] py-2
      font-poppins text-[12.5px] font-semibold text-white shadow-hive-pill ${className}`}
  >
    {children}
    {Icon && <Icon className="h-3.5 w-3.5" />}
  </div>
);

const CollageCard = ({ children, className = '' }) => (
  <div
    className={`rounded-[22px] border border-hive-line bg-white shadow-hive-card ${className}`}
  >
    {children}
  </div>
);

const Avatar = ({ initials, bg, size = 26, text = '9.5px' }) => (
  <div
    className="flex flex-shrink-0 items-center justify-center rounded-full font-extrabold text-hive-navy"
    style={{ width: size, height: size, background: bg, fontSize: text }}
  >
    {initials}
  </div>
);

/* ── Wallchart mock ────────────────────────────────────────────────── */

const C = { empty: 'e', weekend: 'w', annual: 'a', sick: 's', bh: 'b', half: 'h', pending: 'p', bday: 'g' };

const WALLCHART_ROWS = [
  {
    initials: 'AB', name: 'Amrita B.', dept: 'HR', bg: '#FEF2E3',
    cells: [C.annual, C.annual, C.annual, C.annual, C.empty, C.empty, C.weekend, C.weekend, C.empty,
      C.empty, C.empty, C.empty, C.empty, C.weekend, C.weekend, C.annual, C.annual, C.annual],
  },
  {
    initials: 'DA', name: 'Dakarai A.', dept: 'Technical', bg: '#F6EDFD',
    cells: [C.empty, C.sick, C.sick, C.empty, C.empty, C.empty, C.weekend, C.weekend, C.empty,
      C.empty, C.half, C.empty, C.empty, C.weekend, C.weekend, C.empty, C.empty, C.empty],
  },
  {
    initials: 'JC', name: 'June C.', dept: 'Support', bg: '#E7F6EC',
    cells: [C.empty, C.empty, C.bh, C.empty, C.empty, C.empty, C.weekend, C.weekend, C.pending,
      C.pending, C.pending, C.empty, C.empty, C.weekend, C.weekend, C.empty, C.bday, C.empty],
  },
  {
    initials: 'MC', name: 'Myra C.', dept: 'Marketing', bg: '#FBEAE8',
    cells: [C.empty, C.empty, C.empty, C.empty, C.empty, C.empty, C.weekend, C.weekend, C.annual,
      C.annual, C.annual, C.annual, C.annual, C.weekend, C.weekend, C.empty, C.empty, C.empty],
  },
];

const WallchartCell = ({ kind }) => {
  const base = 'h-[22px] rounded flex items-center justify-center';
  switch (kind) {
    case C.annual:
      return <div className={`${base} bg-hive-annual`} />;
    case C.sick:
      return (
        <div className={`${base} bg-hive-sick`}>
          <Thermometer className="h-3 w-3 text-white" />
        </div>
      );
    case C.bh:
      return (
        <div className={`${base} bg-hive-bh`}>
          <Landmark className="h-3 w-3 text-white" />
        </div>
      );
    case C.bday:
      return (
        <div className={`${base} bg-hive-annual`}>
          <Gift className="h-3 w-3 text-white" />
        </div>
      );
    case C.half:
      return (
        <div
          className={base}
          style={{ background: 'linear-gradient(135deg, #F0A32A 50%, #FBF3E3 50%)' }}
        />
      );
    case C.pending:
      return (
        <div
          className={`${base} box-border border-2 border-dashed border-hive-annual`}
          style={{ background: 'rgba(240,163,42,.22)' }}
        />
      );
    case C.weekend:
      return <div className={`${base} bg-hive-weekend`} />;
    default:
      return <div className={`${base} border border-hive-line-softer bg-hive-tile`} />;
  }
};

const WallchartMock = () => (
  <CollageCard className="!rounded-[26px] px-7 py-[26px] shadow-hive-card-lg">
    <div className="flex items-center gap-3 pb-4">
      <Logo size={22} />
      <div className="ml-auto flex gap-3.5 text-[12.5px] font-extrabold text-hive-muted">
        <span className="border-b-2 border-hive-orange pb-0.5 text-hive-orange">Wallchart</span>
        <span>My Calendar</span>
      </div>
    </div>
    <div className="flex flex-col gap-2">
      {WALLCHART_ROWS.map((row) => (
        <div
          key={row.initials}
          className="grid items-center gap-[3px]"
          style={{ gridTemplateColumns: '150px repeat(18, 1fr)' }}
        >
          <div className="flex items-center gap-2">
            <Avatar initials={row.initials} bg={row.bg} />
            <div className="min-w-0">
              <div className="text-xs font-extrabold">{row.name}</div>
              <div className="text-[10px] text-hive-muted">{row.dept}</div>
            </div>
          </div>
          {row.cells.map((kind, i) => (
            <WallchartCell key={i} kind={kind} />
          ))}
        </div>
      ))}
    </div>
  </CollageCard>
);

/* ── Page ──────────────────────────────────────────────────────────── */

const benefits = [
  {
    icon: Users,
    title: 'Filter by department or team',
    description: "Managers see their people; everyone sees who's covering.",
  },
  {
    icon: MousePointerClick,
    title: 'Click any day to request time off',
    description: 'Half days, TOIL and custom leave types included.',
  },
  {
    icon: Bell,
    title: 'Pending requests highlighted',
    description: 'One-click approvals by email — no delays.',
  },
  {
    icon: CalendarDays,
    title: 'Syncs with your calendar',
    description: 'Google, Outlook and Teams — approved leave just appears.',
  },
];

// ⚠️ Every line here must be something the product does TODAY. 12/09/2026: "Mobile &
// desktop apps" (it is a web app that works on a phone), "Burnout alerts" (it is a report
// card, nothing is sent), "Single sign-on (SSO)" (it is Sign in with Google / Microsoft —
// SAML is deferred, DESIGN-signing-in.md) and "Full REST API" (there is none) were reworded
// or replaced. Add a claim back only when the feature ships.
const included = [
  'Unlimited users & departments',
  'Holiday approvals workflow',
  'Shared wallchart calendar',
  'Works on any phone or laptop',
  'Custom leave types',
  'Backup approvers',
  'Absence reports & insights',
  'Burnout risk report',
  'Sign in with Google or Microsoft',
  'Calendar feed for any app (iCal)',
  'Payroll export',
  'TOIL tracking',
];

// `reviews` and `heroQuotes` were removed 12/09/2026 — no HolidayHive customer had said any
// of it. Put real quotes back here when there are some, with the person's permission.


const digest = [
  { initials: 'AB', name: 'Amrita Biswas', when: 'All week', type: 'Annual Leave', bg: '#FEF2E3', chipBg: '#FEF2E3', chipFg: '#B15E07' },
  { initials: 'DA', name: 'Dakarai Ababio', when: 'Today', type: 'Sickness', bg: '#F6EDFD', chipBg: '#FBEAE8', chipFg: '#C0392B' },
  { initials: 'JC', name: 'June Chen', when: 'All week', type: 'Maternity', bg: '#E7F6EC', chipBg: '#F6EDFD', chipFg: '#8B5CF6' },
];

const LandingPage = () => {
  const trialDays = useTrialDays();
  return (
  <div className="min-h-screen overflow-hidden bg-hive-warm font-nunito text-base text-hive-navy">
    {/* ── Nav — shared with the legal pages, see components/MarketingChrome.jsx ── */}
    <MarketingNav />

    {/* ── Hero ──────────────────────────────────────────────────── */}
    <section className="mx-auto max-w-[880px] px-8 pb-10 pt-16 text-center">
      <h1 className="m-0 font-poppins text-4xl font-bold leading-[1.15] sm:text-5xl lg:text-[58px]">
        The friendliest <span className="text-hive-orange">staff holiday</span> and{' '}
        <span className="text-hive-bh">absence</span> tracker
      </h1>
      <p className="mx-auto mt-[26px] max-w-[640px] text-[19px] leading-[1.65] text-hive-muted">
        Switching to HolidayHive makes booking time off a breeze. No more paper forms or messy
        spreadsheets — just a beautiful, up-to-date record of who's off, so you can plan better and
        work smarter.
      </p>
      <div className="mt-[34px] flex flex-col items-center gap-2.5">
        <Link to="/register">
          <Button
            className={`${CTA_PRIMARY} h-auto px-[42px] py-[18px] text-lg shadow-hive-cta-lg`}
            data-testid="hero-cta-btn"
          >
            Give HolidayHive a try
          </Button>
        </Link>
        <div className="text-[13px] font-bold text-hive-muted">
          {/* The trial length comes from Superadmin → Trials (H6, 13/10/2026). This said
              "Free for one month" as fixed text whatever the setting was. */}
          {trialDays ? `Free for ${trialDays} days` : 'Free trial'} — no card required · {MONTHLY}/user/month after
        </div>
      </div>
      {/* Testimonial strip removed 12/09/2026: the page is now public on holidayhive.co.uk
          and HolidayHive has no customers yet, so there are no genuine quotes to show.
          Real quotes go back here the day there are some. */}
    </section>

    {/* ── Product collage ───────────────────────────────────────── */}
    <section className="relative mx-auto max-w-[1240px] px-8 pb-[90px] pt-[30px]">
      {/* soft colour blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-[2%] top-[60px] h-[460px] w-[460px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(240,192,64,.35), rgba(240,192,64,0) 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute right-[4%] top-0 h-[520px] w-[520px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(247,135,31,.22), rgba(247,135,31,0) 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[60px] left-[38%] h-[420px] w-[420px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(181,123,238,.2), rgba(181,123,238,0) 70%)' }}
      />

      <div className="relative grid items-start gap-7 lg:grid-cols-[340px_1fr]">
        {/* Booking form mock */}
        <div className="relative mt-10">
          <CalloutPill icon={Send} className="-top-[18px] left-1/2 -translate-x-1/2">
            Book leave in seconds
          </CalloutPill>
          <CollageCard className="!rounded-[26px] p-6 shadow-hive-card-lg">
            <div className="flex items-center justify-between pb-4">
              <div className="text-sm font-extrabold text-hive-muted">New request</div>
              <div className="rounded-full bg-hive-orange px-4 py-1.5 text-[11.5px] font-extrabold text-white">
                SEND
              </div>
            </div>
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center gap-2.5 rounded-xl border border-hive-line-soft px-3.5 py-3">
                <Avatar initials="SM" bg="#FBF6EF" text="10px" />
                <div className="text-[13.5px] font-extrabold">Sarah Mitchell</div>
                <ChevronRight className="ml-auto h-4 w-4 text-hive-disabled" />
              </div>
              <div className="flex items-center gap-2.5 rounded-xl border border-hive-line-soft px-3.5 py-3">
                <Sun className="h-4 w-4 text-hive-orange" />
                <div className="text-[13.5px] font-extrabold">Annual leave</div>
                <ChevronRight className="ml-auto h-4 w-4 text-hive-disabled" />
              </div>
              <div className="flex items-center gap-2.5 rounded-xl border border-hive-line-soft px-3.5 py-3">
                <Palmtree className="h-4 w-4 text-hive-annual" />
                <div className="text-[13.5px] font-bold text-hive-muted">A family holiday to Cornwall</div>
              </div>
              <div
                className="grid gap-1.5 rounded-xl border border-hive-line-soft px-3.5 py-3 text-[13px] text-hive-muted"
                style={{ gridTemplateColumns: '44px 1fr 1fr' }}
              >
                <b className="text-hive-navy">From</b>
                <span>Mon, 16 Aug</span>
                <span className="font-extrabold text-hive-orange">Morning</span>
                <b className="text-hive-navy">To</b>
                <span>Fri, 20 Aug</span>
                <span className="font-extrabold text-hive-orange">End of day</span>
              </div>
              <div className="text-center text-[12.5px] font-bold text-hive-muted">Deduction: 5 days</div>
            </div>
          </CollageCard>
        </div>

        <div className="flex flex-col gap-6">
          {/* Wallchart mock */}
          <div className="relative">
            <CalloutPill icon={CalendarDays} className="-top-4 right-10">
              See who's off on the Wallchart
            </CalloutPill>
            <WallchartMock />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Approval email mock */}
            <div className="relative">
              <CalloutPill icon={Check} className="-bottom-3.5 left-1/2 -translate-x-1/2">
                Easy request approval
              </CalloutPill>
              <CollageCard className="px-6 py-[22px]">
                <div className="text-[13.5px] text-hive-muted">
                  Hi Dan, <b className="text-hive-navy">Sarah Mitchell</b> has requested time off.
                </div>
                <div className="my-3.5 flex items-center gap-3">
                  <Avatar initials="SM" bg="#FEF2E3" size={36} text="12px" />
                  <div className="rounded-xl bg-hive-cream px-3.5 py-2.5 text-[13px] text-hive-muted">
                    “A family holiday to Cornwall”
                  </div>
                </div>
                <div className="flex items-center gap-1.5 pb-3.5 text-[13px] text-hive-muted">
                  <Sun className="h-3.5 w-3.5 text-hive-orange" />
                  Annual leave · Mon 16 – Fri 20 Aug · <b className="text-hive-navy">5 days</b>
                </div>
                <div className="flex gap-2.5">
                  <button
                    type="button"
                    className="flex-1 rounded-[10px] bg-hive-success-bg py-[11px] text-[13.5px] font-extrabold text-hive-success-fg"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="flex-1 rounded-[10px] bg-hive-danger-bg py-[11px] text-[13.5px] font-extrabold text-hive-danger-fg"
                  >
                    Decline
                  </button>
                </div>
              </CollageCard>
            </div>

            {/* Who's-off digest mock */}
            <div className="relative">
              <CalloutPill icon={Bell} className="-bottom-3.5 left-1/2 -translate-x-1/2">
                Daily or weekly digest
              </CalloutPill>
              <CollageCard className="px-6 py-[22px]">
                <div className="pb-3.5 text-[13px] font-extrabold tracking-[.05em] text-hive-muted">
                  WHO'S OFF THIS WEEK
                </div>
                <div className="flex flex-col gap-3">
                  {digest.map((p) => (
                    <div key={p.initials} className="flex items-center gap-2.5">
                      <Avatar initials={p.initials} bg={p.bg} size={30} text="10.5px" />
                      <div className="flex-1">
                        <div className="text-[13px] font-extrabold">{p.name}</div>
                        <div className="text-[11.5px] text-hive-muted">{p.when}</div>
                      </div>
                      <span
                        className="rounded-full px-2.5 py-1 text-[10.5px] font-extrabold"
                        style={{ background: p.chipBg, color: p.chipFg }}
                      >
                        {p.type}
                      </span>
                    </div>
                  ))}
                </div>
              </CollageCard>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── No spreadsheets. No forms. ────────────────────────────── */}
    <section
      id="why"
      className="scroll-mt-8 border-y border-hive-line bg-hive-cream px-8 py-[90px]"
    >
      <div className="mx-auto grid max-w-[1240px] items-center gap-[60px] lg:grid-cols-[1fr_1.1fr]">
        <div>
          <h2 className="m-0 font-poppins text-4xl font-bold leading-[1.12] lg:text-[44px]">
            No spreadsheets.
            <br />
            No forms.
          </h2>
          <p className="mb-7 mt-5 text-[17px] leading-[1.7] text-hive-muted">
            Say goodbye to tedious spreadsheets and paper forms. HolidayHive streamlines the whole
            process — log in, request your holiday, and let the hive handle the rest. Save yourself
            some precious time and energy.
          </p>
          <Link to="/register">
            <Button className={`${CTA_PRIMARY} h-auto px-8 py-4 text-base shadow-hive-cta`}>
              Start free trial
            </Button>
          </Link>
        </div>
        <div className="flex flex-col gap-3.5">
          {benefits.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="flex items-start gap-3.5 rounded-[18px] border border-hive-line bg-white px-6 py-5"
            >
              <div className="hex-clip flex h-11 w-11 flex-shrink-0 items-center justify-center bg-hive-pending-bg">
                <Icon className="h-[19px] w-[19px] text-hive-orange" />
              </div>
              <div>
                <div className="font-poppins text-base font-semibold">{title}</div>
                <div className="mt-0.5 text-sm text-hive-muted">{description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Pricing ───────────────────────────────────────────────── */}
    <section id="pricing" className="relative scroll-mt-8 overflow-hidden bg-hive-navy px-8 py-[84px]">
      <div
        aria-hidden
        className="hex-clip pointer-events-none absolute -left-[50px] -top-[50px] h-[220px] w-[220px]"
        style={{ background: 'rgba(247,135,31,.08)' }}
      />
      <div
        aria-hidden
        className="hex-clip pointer-events-none absolute -bottom-[60px] -right-10 h-[260px] w-[260px]"
        style={{ background: 'rgba(247,135,31,.06)' }}
      />
      <div className="relative mx-auto grid max-w-[1240px] items-center gap-[60px] lg:grid-cols-[1fr_1.1fr]">
        <div className="text-white">
          <h2 className="m-0 font-poppins text-4xl font-bold leading-[1.15] lg:text-[40px]">
            Simple pricing. <span className="text-hive-orange">No tiers.</span>
          </h2>
          <p className="mt-[18px] text-[17px] leading-[1.65] text-hive-muted-cool">
            Other providers charge £2–3 per user, then lock features behind expensive tiers. We
            include everything for one flat price.
          </p>
          <div className="my-7 flex items-baseline gap-2.5">
            <span className="font-poppins text-[72px] font-bold leading-none text-hive-orange">{MONTHLY}</span>
            <span className="text-xl text-hive-muted-cool">/user/month</span>
          </div>
          <Link to="/register">
            <Button className={`${CTA_PRIMARY} h-auto px-[30px] py-4 text-base`} data-testid="pricing-cta-btn">
              Start Your Free Trial
            </Button>
          </Link>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {included.map((item) => (
            <div key={item} className="flex items-center gap-2.5 text-[14.5px] font-semibold text-hive-on-navy">
              <Check className="h-4 w-4 flex-shrink-0 text-hive-orange" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Closing call to action ────────────────────────────────── */}
    {/* This used to be the "reviews" section. The heading, the "view all reviews" link and
        the four review cards were removed 12/09/2026 when the page went public on
        holidayhive.co.uk: none of it was said by a HolidayHive customer. The section id
        is kept so the "About" link in the header still lands somewhere. */}
    <section id="reviews" className="mx-auto max-w-[1240px] scroll-mt-8 px-8 py-[90px] text-center">
      <div className="mx-auto max-w-[680px]">
        <h2 className="m-0 font-poppins text-3xl font-bold leading-[1.2] lg:text-[40px]">
          Built for teams like yours to book time off <span className="text-hive-orange">without the spreadsheet</span>
        </h2>
        <p className="mb-[30px] mt-[18px] text-[17px] leading-[1.65] text-hive-muted">
          Agencies, surgeries, shops, charities, workshops — if your team books holiday by
          email and a spreadsheet, HolidayHive is the simpler way.
        </p>
        <Link to="/register">
          <Button
            className={`${CTA_PRIMARY} h-auto px-[42px] py-[18px] text-lg shadow-hive-cta-lg`}
            data-testid="final-cta-btn"
          >
            Start your free trial
          </Button>
        </Link>
        <div className="mt-3 text-[13px] font-bold text-hive-muted">
          Free for one month — no card required
        </div>
      </div>
    </section>

    {/* ── Your calendar data ────────────────────────────────────── */}
    {/* Required wording for Google OAuth verification (12/09/2026): the homepage on the
        consent screen must say what the app does with Google user data, in plain terms,
        and link to the privacy policy. Keep it true — the reviewer compares it to the
        scopes requested. See GOOGLE-VERIFICATION.md. */}
    <section id="calendar-data" className="mx-auto max-w-[880px] scroll-mt-8 px-8 pb-[90px] pt-4">
      <div className="rounded-[18px] border border-hive-line bg-white px-7 py-7 sm:px-9">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 flex-shrink-0 text-hive-orange" />
          <h2 className="m-0 font-poppins text-2xl font-bold">How HolidayHive uses your calendar</h2>
        </div>
        <p className="mt-4 text-[15.5px] leading-[1.7] text-hive-muted">
          Connecting a Google or Microsoft calendar is optional. If you connect one,
          HolidayHive asks only for permission to manage events in your calendar and uses it
          for one thing: adding your <strong>approved</strong> leave to your own calendar as
          all-day events, and updating or removing those events if the leave changes or is
          cancelled.
        </p>
        <p className="mt-3 text-[15.5px] leading-[1.7] text-hive-muted">
          HolidayHive does not read your other events, does not share your calendar data with
          anyone, and does not use it for advertising. You can disconnect at any time from
          your profile: HolidayHive deletes the key it was given and can no longer reach your
          calendar. Events it has already added stay in your calendar — they are yours to
          keep or delete. HolidayHive's use of information received from Google APIs adheres
          to the{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            className="font-bold text-hive-orange hover:text-hive-orange-hover"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
        <p className="mt-3 text-[15.5px] leading-[1.7] text-hive-muted">
          The full picture of what we collect and why is in our{' '}
          <Link to="/privacy" className="font-bold text-hive-orange hover:text-hive-orange-hover">
            Privacy policy
          </Link>
          .
        </p>
      </div>
    </section>

    {/* ── Footer — shared with the legal pages, see components/MarketingChrome.jsx ── */}
    <MarketingFooter />
  </div>
  );
};

export default LandingPage;
